import ChessRestClient from '@/Api/ChessRestClient';
import RandomApiClient from '../../Api/RandomApiClient';
import { LoginResponse, SendOtpResponse } from './types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import {
  getFormattedTimeZone,
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '../../Utils/CommonUtils';
import useChatRestClient from '@/Api/ChatRestClient';
import { useRoute } from '@react-navigation/native';
import { useUserPlanStore } from '@/Store/userPlanStore';
import {hydrateLoggedInUserStore} from '@/Store/loggedInUserstore';
import useOneClickClient from '@/Api/oneClickClient';
import {applyFastPathGates} from './loginApi.helpers';

export default function useLogin(
  handleOnSuccess?: (data: any) => void,
  handleOnError?: (data: any) => void,
) {
  const mutation = useMutation<
    LoginResponse,
    Error,
    {
      username: string;
      password: string;
      email: string;
      isLogin: boolean;
    }
  >(
    async loginData => {
      console.log('Calling Login / Registration API..');
      const { username, password, email, isLogin } = loginData;
      if (isLogin) {
        console.log('About to call login api ..');
        const response = await ChessRestClient.post('v1/login_api/', {
          username,
          password,
        });
        return response.data;
      } else {
        console.log('About to call register api ..');
        const response = await ChessRestClient.post('v1/register_api/', {
          username,
          password,
          email,
        });
        return response.data;
      }
    },
    {
      onSuccess: data => {
        if (handleOnSuccess && data.success) {
          handleOnSuccess(data);
        } else if (handleOnError) {
          handleOnError(data);
        }
      },
    },
  );

  return {
    ...mutation,
  };
}

// TODO: temp code, cleanup

// The whole session hinges on this one call: if it blips, the user is shown the
// login screen despite holding a valid token — the "logged in, then instantly
// logged out" report. One retry covers a transient network/5xx hiccup. A real
// auth failure is rethrown immediately; retrying that would only stall the UI.
const loadSummary = async (user_timezone: string) => {
  const params = {website: 'learning', user_timezone};
  try {
    return await ChessRestClient.get('dashboard/v1/summary/', {params});
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      throw err;
    }
    await new Promise(resolve => setTimeout(resolve, 700));
    return ChessRestClient.get('dashboard/v1/summary/', {params});
  }
};

export const useLoginState = reload => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const { setPlanDetails } = useUserPlanStore();
  const [error, setError] = useState('');
  const OneClickClient = useOneClickClient();
  useEffect(() => {
    // Guards every setState below. This effect re-runs whenever `reload`
    // toggles (i.e. on every onLoginStateChange), and nothing used to stop an
    // older run from finishing after a newer one — so the no-token run from
    // first mount could land AFTER the post-login run and flip a freshly
    // signed-in user back to logged-out, token still in storage.
    let cancelled = false;

    const fetchData = async function () {
      try {
        setLoading(true);

        const extractOneClickTokenFromUrl = () => {
          try {
            const loc = window?.location;

            const url = new URL(window?.location.href);
            let token = url?.searchParams.get('token');
            // fallback: token in hash fragment (#token=...)
            if (!token && loc?.hash) {
              const hash = loc?.hash.replace(/^#/, '');
              const hp = new URLSearchParams(hash);
              token = hp.get('token') || null;
            }
            return token;
          } catch (e) {
            return null;
          }
        };

        // 1) If there's a one-click token in URL, attempt one-click login first
        const oneClickToken = extractOneClickTokenFromUrl();
        if (oneClickToken) {
          // remove token from URL right away to avoid leak/reuse
          try {
            const url = new URL(window?.location.href);
            let token = url?.searchParams.get('token');
            localStorage.setItem('oneClickToken', token || '');
            url?.searchParams.delete('token');
            if (window?.location.hash) {
              const hash = window?.location?.hash?.replace(/^#/, '');
              const hp = new URLSearchParams(hash);
              if (hp.has('token')) {
                hp.delete('token');
                const newHash = hp.toString();
                url.hash = newHash ? `#${newHash}` : '';
              }
            }
            window?.history.replaceState({}, document.title, url.toString());
          } catch (e) {
            console.warn('Failed to clean token from URL', e);
          }

          // Call one-click login endpoint
          try {
            const oneClickRes = await OneClickClient.post(
              '/auth/v1/oneClickLogin',
              {
                token: oneClickToken,
              },
            );

            const apiData = oneClickRes?.data;

            // If JWT flow: save tokens (overwrite any previous tokens)
            if (apiData?.access_token && apiData?.refresh_token) {
              await setLocalStorage(
                'refresh_token',
                apiData.refresh_token,
                true,
              );
              await setLocalStorage('access_token', apiData.access_token, true);
              if (apiData?.user_key) {
                await setLocalStorage('userkey', apiData.user_key, true);
              }
              await removeLocalStorage('isEvaluate');
              await removeLocalStorage('tournamentId');
              localStorage.setItem('loginEvent', Date.now());
              // proceed to dashboard fetch below
            } else {
              // Unexpected payload: abort and treat as not logged in
              console.warn(
                'oneClickLogin returned unexpected payload',
                apiData,
              );
              setData({});
              setIsUserLoggedIn(false);
              return false;
            }
          } catch (err) {
            console.error(
              'One-click login failed:',
              err?.response?.data || err.message || err,
            );
            setData({});
            setIsUserLoggedIn(false);
            return false;
          }
        }

        const user_timezone = getFormattedTimeZone(false, true);
        const token = await getLocalStorage('access_token');
        console.log('useLoginState token: ', token);

        if (cancelled) {
          return;
        }

        if (token) {
          let response;
          try {
            response = await loadSummary(user_timezone);
          } catch (err: any) {
            if (cancelled) {
              return;
            }
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
              // The token really is dead. Clear it so the next load doesn't
              // retry with it, and send them to Login — this is the ONE case
              // where landing on the login screen is correct.
              await removeLocalStorage('access_token');
              await removeLocalStorage('refresh_token');
              setData({});
              setIsUserLoggedIn(false);
              return;
            }
            // Anything else (network blip, 5xx, timeout) is not a sign-out.
            // Rethrow so the outer catch shows the error screen and the
            // session — token included — survives a refresh.
            throw err;
          }

          if (cancelled) {
            return;
          }

          if (response?.status === 200) {
            const gatedPayload = {
              ...response.data,
              data: {
                ...response.data?.data,
                user: applyFastPathGates(response.data?.data?.user),
              },
            };
            setData(gatedPayload);
            hydrateLoggedInUserStore(gatedPayload.data);
            console.log('useLoginState response: ', response.data.data.user);
            const updateLocalStorage = async () => {
              try {
                await setLocalStorage(
                  'user_details',
                  JSON.stringify(gatedPayload?.data?.user),
                  true,
                );
                setPlanDetails?.(
                  response?.data?.data?.user_subscription_details,
                );
              } catch (error) {
                console.error('Failed to set local storage:', error);
                setError(`API request failed with status ${response.status}`);
              }
            };
            await updateLocalStorage();
            if (!cancelled) {
              setIsUserLoggedIn(true);
            }
          } else {
            // Previously this fell through silently, leaving isUserLoggedIn at
            // its initial `false` with no error set — i.e. the login screen,
            // despite holding a valid token. We have a token here, so an
            // unexpected status is an app error, not a sign-out.
            setError('We could not load your profile. Please refresh to try again.');
          }
        } else {
          setData({});
          let currentPath = window?.location.pathname;
          if (currentPath?.startsWith('/')) {
            currentPath = currentPath?.slice(1);
          }
          console.log('currentPath', currentPath);
          await setLocalStorage('current_path', currentPath);
          if (!cancelled) {
            setIsUserLoggedIn(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setError('An unexpected error occurred. We will resolve it shortly.');
        }
        // NOT rethrown: fetchData() is called bare, so a rethrow was only ever
        // producing an unhandled promise rejection. The error state above is
        // what the UI actually reads.
      } finally {
        // Guarded: a cancelled (superseded) run clearing `loading` would drop
        // the app out of its loader mid-login and flash the login screen while
        // the current run is still fetching.
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchData();

    return () => {
      cancelled = true;
    };
  }, [reload]);

  return { loading, data, isUserLoggedIn, error };
};

export const sendOtp = (
  handleOnSuccess?: (data: any) => void,
  handleOnError?: (data: any) => void,
) => {
  const ChatRestClient = useChatRestClient();
  const mutation = useMutation<
    SendOtpResponse,
    Error,
    {
      mobile: string;
      countryCode: string;
      source: string;
      password?: string;
      admin_login?: boolean;
    }
  >(
    async loginData => {
      const { mobile, countryCode, password, admin_login } = loginData;
      const response = await ChatRestClient.post('auth/v1/send_otp', {
        mobile_number: mobile,
        country_code: countryCode,
        source: 'learn',
        password: password,
        admin_login: admin_login,
      });
      return response.data;
    },
    {
      onSettled: (data, error) => {
        if (handleOnSuccess && data) {
          handleOnSuccess(data);
        } else if (handleOnError && error) {
          handleOnError(error);
        }
      },
    },
  );

  return {
    ...mutation,
  };
};

export const verifyOtp = (
  handleOnSuccess?: (data: any) => void,
  handleOnError?: (data: any) => void,
) => {
  const ChatRestClient = useChatRestClient();
  const mutation = useMutation<
    SendOtpResponse,
    Error,
    {
      mobile: string;
      countryCode: string;
      otp: string;
      terms?: boolean;
      admin_login?: boolean;
    }
  >(
    async loginData => {
      const { mobile, countryCode, otp, terms, admin_login } = loginData;
      // friend_referral_code (?ref=, peer-to-peer) takes priority over referral_code
      // (?ccpplearn=, partner attribution) -- backend only cares about the resolved
      // code, not which UI param it came from.
      const friend_referral_code = await getLocalStorage('friend_referral_code');
      const referral_code = friend_referral_code || (await getLocalStorage('referral_code'));
      const currentPath = await getLocalStorage('current_path');
      const response = await ChatRestClient.post('auth/v1/verify_otp', {
        mobile_number: mobile,
        country_code: countryCode,
        otp,
        sender: 'ccl',
        tnc: terms,
        source: currentPath === 'csoc-new-payment' ? 'checkout_page' : 'learn',
        referral_code: referral_code ? referral_code : '',
        admin_login: admin_login,
      });
      return response.data;
    },
    {
      onSettled: (data, error) => {
        if (handleOnSuccess && data) {
          handleOnSuccess(data);
        } else if (handleOnError && error) {
          handleOnError(error);
        }
      },
    },
  );

  return {
    ...mutation,
  };
};

export const getTableauWorkbooks = (
  handleOnSuccess?: (data: any) => void,
  handleOnError?: (data: any) => void,
) => {
  const mutation = useMutation<any, Error>(
    async () => {
      const response = await ChessRestClient.get('learning/tableau_views/');
      return response.data;
    },
    {
      onSettled: (data, error) => {
        if (handleOnSuccess && data) {
          handleOnSuccess(data);
        } else if (handleOnError && error) {
          handleOnError(error);
        }
      },
    },
  );

  return {
    ...mutation,
  };
};
